import React, { useState, useEffect } from 'react';
import { FaUser, FaEnvelope, FaPhone, FaComment } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { sendQuoteRequestEmail, QuoteFormData } from '../../../services/quoteService';
import './QuoteModal.css';

interface QuoteModalProps {
    isOpen: boolean;
    onClose: () => void;
    serviceTitle: string;
    subserviceTitle: string;
    productTitle?: string;
}

const QuoteModal: React.FC<QuoteModalProps> = ({ isOpen, onClose, serviceTitle, subserviceTitle, productTitle }) => {
    const { t } = useTranslation();
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        message: ''
    });
    const [modalActive, setModalActive] = useState(false);
    const [isSending, setIsSending] = useState(false);
    const [sendStatus, setSendStatus] = useState<'idle' | 'success' | 'error'>('idle');

    useEffect(() => {
        if (isOpen) {
            setModalActive(true);
        } else {
            setTimeout(() => setModalActive(false), 300);
        }
    }, [isOpen]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData(prevState => ({ ...prevState, [name]: value }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSending(true);
        
        const quoteData: QuoteFormData = {
            name: formData.name,
            email: formData.email,
            phone: formData.phone,
            service: serviceTitle,
            subservice: subserviceTitle,
            product: productTitle || '',
            message: formData.message
        };

        const success = await sendQuoteRequestEmail(quoteData);
        setSendStatus(success ? 'success' : 'error');
        setIsSending(false);

        setTimeout(() => {
            setSendStatus('idle');
            if (success) {
                onClose();
            }
        }, success ? 2000 : 3000);
    };

    if (!modalActive && !isOpen) return null;

    return (
        <div className={`quote-modal ${modalActive ? 'active' : ''}`}>
            <div className="quote-modal-content">
                <div className="quote-modal-header">
                    <h2 className="quote-modal-title">{t('quote.request_quote', 'Demander un devis')}</h2>
                    <button className="quote-modal-close" onClick={onClose}>&times;</button>
                </div>
                <div className="quote-modal-body">
                    <h5>{t('quote.form.service', 'Service')}: {serviceTitle}</h5>
                    <h6>{t('quote.form.subService', 'Sous-service')}: {subserviceTitle}</h6>
                    {sendStatus === 'success' && (
                        <div className="alert alert-success mt-3" role="alert">
                            {t('quote.form.success', 'Votre demande a été envoyée avec succès. Nous vous contacterons bientôt.')}
                        </div>
                    )}
                    {sendStatus === 'error' && (
                        <div className="alert alert-danger mt-3" role="alert">
                            {t('quote.form.error', 'Échec de l\'envoi de votre demande. Veuillez réessayer.')}
                        </div>
                    )}
                    <form onSubmit={handleSubmit}>
                        <div className="form-group-modal">
                            <label htmlFor="formName" className="form-label">{t('quote.name', 'Nom')}</label>
                            <input
                                type="text"
                                className="form-control"
                                id="formName"
                                placeholder={t('quote.form.name', 'Entrez votre nom')}
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                required
                                disabled={isSending}
                            />
                            <span className="form-icon"><FaUser /></span>
                        </div>

                        <div className="form-group-modal">
                            <label htmlFor="formEmail" className="form-label">{t('quote.email', 'Email')}</label>
                            <input
                                type="email"
                                className="form-control"
                                id="formEmail"
                                placeholder={t('quote.form.email', 'Entrez votre email')}
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                required
                                disabled={isSending}
                            />
                            <span className="form-icon"><FaEnvelope /></span>
                        </div>

                        <div className="form-group-modal">
                            <label htmlFor="formPhone" className="form-label">{t('quote.phone', 'Téléphone')}</label>
                            <input
                                type="tel"
                                className="form-control"
                                id="formPhone"
                                placeholder={t('quote.form.phone', 'Entrez votre numéro de téléphone')}
                                name="phone"
                                value={formData.phone}
                                onChange={handleChange}
                                disabled={isSending}
                            />
                            <span className="form-icon"><FaPhone /></span>
                        </div>

                        <div className="form-group-modal">
                            <label htmlFor="formMessage" className="form-label">{t('quote.message', 'Message')}</label>
                            <textarea
                                className="form-control"
                                id="formMessage"
                                rows={3}
                                placeholder={t('quote.form.messagePlaceholder', 'Décrivez votre demande')}
                                name="message"
                                value={formData.message}
                                onChange={handleChange}
                                required
                                disabled={isSending}
                            />
                            <span className="form-icon"><FaComment /></span>
                        </div>

                        <div className="quote-modal-footer">
                            <button 
                                type="submit" 
                                className="quote-modal-submit"
                                disabled={isSending}
                            >
                                {isSending ? t('quote.form.sending', 'Envoi en cours...') : t('quote.form.submit', 'Envoyer ma demande')}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default QuoteModal;
